import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { interviewAPI } from '../services/api';
import { CreditCounter } from './CreditComponents';
import logo from '../assets/logo.png';

const MockInterview = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  // State management
  const [step, setStep] = useState('setup'); // setup, interview, feedback, complete
  const [jobDescription, setJobDescription] = useState('');
  const [numQuestions, setNumQuestions] = useState(5);
  const [sessionId, setSessionId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Check authentication
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Start interview
  const handleStartInterview = async () => {
    if (!jobDescription.trim()) {
      setError('Please enter a job description');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await interviewAPI.startInterview(jobDescription, numQuestions);
      setSessionId(result.session_id);
      setQuestions(result.questions);
      setStep('interview');
    } catch (err) {
      if (err.response?.status === 402) {
        setError('Insufficient credits. You need 1 credit to start a mock interview.');
      } else {
        setError(err.response?.data?.detail || 'Failed to start interview. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (err) {
      setError('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async () => {
    if (!audioBlob) return;

    setTranscribing(true);
    try {
      const result = await interviewAPI.transcribeAudio(sessionId, audioBlob);
      setCurrentAnswer(result.transcript);
      setAudioBlob(null);
    } catch (err) {
      setError('Failed to transcribe audio. Please try typing your answer.');
    } finally {
      setTranscribing(false);
    }
  };

  // Submit answer
  const handleSubmitAnswer = async () => {
    if (!currentAnswer.trim() || currentAnswer.length < 10) {
      setError('Please provide a more detailed answer (at least 10 characters)');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await interviewAPI.submitAnswer(sessionId, currentQuestionIndex, currentAnswer);
      
      // Store the answer with feedback
      setAnswers([...answers, {
        question: questions[currentQuestionIndex],
        answer: currentAnswer,
        feedback: result.feedback
      }]);
      
      setFeedback(result.feedback);
      setStep('feedback');
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit answer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Move to next question
  const handleNextQuestion = () => {
    if (currentQuestionIndex + 1 >= questions.length) {
      handleCompleteInterview();
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setCurrentAnswer('');
      setFeedback(null);
      setAudioBlob(null);
      setStep('interview');
    }
  };

  // Complete interview
  const handleCompleteInterview = async () => {
    setLoading(true);
    try {
      const result = await interviewAPI.completeInterview(sessionId);
      setSummary(result);
      setStep('complete');
    } catch (err) {
      setError('Failed to complete interview. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Reset for new interview
  const handleNewInterview = () => {
    setStep('setup');
    setJobDescription('');
    setSessionId(null);
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setAnswers([]);
    setCurrentAnswer('');
    setFeedback(null);
    setSummary(null);
    setError(null);
  };

  // Render setup step
  const renderSetup = () => (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl shadow-lg p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🎤</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Mock Interview</h2>
          <p className="text-gray-600 mt-2">Practice with AI-generated questions tailored to your target role</p>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Job Description
            </label>
            <textarea
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              placeholder="Paste the job description here..."
              className="w-full h-48 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Number of Questions
            </label>
            <div className="flex gap-3">
              {[3, 5, 10].map((num) => (
                <button
                  key={num}
                  onClick={() => setNumQuestions(num)}
                  className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                    numQuestions === num
                      ? 'bg-purple-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {num} Questions
                </button>
              ))}
            </div>
          </div>

          <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
            <div className="flex items-center gap-2 text-purple-800">
              <span className="text-lg">💡</span>
              <span className="text-sm font-medium">This will use 1 credit from your balance</span>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl text-sm">
              {error}
            </div>
          )}

          <button
            onClick={handleStartInterview}
            disabled={loading || !jobDescription.trim()}
            className="w-full py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Generating Questions...
              </span>
            ) : (
              'Start Interview'
            )}
          </button>
        </div>
      </div>
    </div>
  );

  // Render interview step
  const renderInterview = () => {
    const currentQuestion = questions[currentQuestionIndex];
    
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          {/* Progress */}
          <div className="mb-8">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Question {currentQuestionIndex + 1} of {questions.length}</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                currentQuestion.type === 'technical' 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-green-100 text-green-700'
              }`}>
                {currentQuestion.type}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-purple-600 to-blue-600 h-2 rounded-full transition-all"
                style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Question */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {currentQuestion.question}
            </h3>
          </div>

          {/* Answer Input */}
          <div className="space-y-4">
            {/* Voice Recording */}
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
              <button
                onClick={isRecording ? stopRecording : startRecording}
                className={`p-4 rounded-full transition-all ${
                  isRecording 
                    ? 'bg-red-500 text-white animate-pulse' 
                    : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                }`}
              >
                {isRecording ? (
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <rect x="6" y="6" width="12" height="12" rx="2" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                  </svg>
                )}
              </button>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-700">
                  {isRecording ? 'Recording... Click to stop' : 'Click to record your answer'}
                </p>
                <p className="text-xs text-gray-500">Or type your answer below</p>
              </div>
              {audioBlob && !isRecording && (
                <button
                  onClick={transcribeAudio}
                  disabled={transcribing}
                  className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  {transcribing ? 'Transcribing...' : 'Use Recording'}
                </button>
              )}
            </div>

            {/* Text Input */}
            <textarea
              value={currentAnswer}
              onChange={(e) => setCurrentAnswer(e.target.value)}
              placeholder="Type your answer here..."
              className="w-full h-40 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
            />

            {error && (
              <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl text-sm">
                {error}
              </div>
            )}

            <button
              onClick={handleSubmitAnswer}
              disabled={loading || !currentAnswer.trim()}
              className="w-full py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Getting Feedback...' : 'Submit Answer'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render feedback step
  const renderFeedback = () => (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-2xl shadow-lg p-8">
        {/* Score */}
        <div className="text-center mb-8">
          <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full text-3xl font-bold ${
            feedback.score >= 8 ? 'bg-green-100 text-green-700' :
            feedback.score >= 6 ? 'bg-yellow-100 text-yellow-700' :
            'bg-red-100 text-red-700'
          }`}>
            {feedback.score}/10
          </div>
          <p className="text-gray-600 mt-2">Answer Score</p>
        </div>

        {/* Feedback Cards */}
        <div className="space-y-4">
          <div className="bg-green-50 rounded-xl p-4 border border-green-100">
            <div className="flex items-start gap-3">
              <span className="text-green-600 text-xl">✓</span>
              <div>
                <h4 className="font-semibold text-green-800">Strengths</h4>
                <p className="text-green-700 text-sm mt-1">{feedback.strengths}</p>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
            <div className="flex items-start gap-3">
              <span className="text-amber-600 text-xl">↑</span>
              <div>
                <h4 className="font-semibold text-amber-800">Areas for Improvement</h4>
                <p className="text-amber-700 text-sm mt-1">{feedback.improvements}</p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
            <div className="flex items-start gap-3">
              <span className="text-blue-600 text-xl">💡</span>
              <div>
                <h4 className="font-semibold text-blue-800">Pro Tip</h4>
                <p className="text-blue-700 text-sm mt-1">{feedback.tip}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Next Button */}
        <button
          onClick={handleNextQuestion}
          className="w-full mt-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all"
        >
          {currentQuestionIndex + 1 >= questions.length ? 'Complete Interview' : 'Next Question →'}
        </button>
      </div>
    </div>
  );

  // Render complete step
  const renderComplete = () => (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-2xl shadow-lg p-8">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-4xl">🎉</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Interview Complete!</h2>
          <p className="text-gray-600 mt-2">Here&apos;s your performance summary</p>
        </div>

        {/* Overall Score */}
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6 mb-6 text-center">
          <p className="text-sm text-purple-600 font-medium mb-1">Overall Score</p>
          <p className="text-5xl font-bold text-purple-900">{summary?.overall_score}/10</p>
          <p className="text-sm text-purple-600 mt-1">
            {summary?.answered_questions} of {summary?.total_questions} questions answered
          </p>
        </div>

        {/* Summary */}
        <div className="bg-gray-50 rounded-xl p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">AI Coach Summary</h3>
          <p className="text-gray-700 whitespace-pre-line">{summary?.summary}</p>
        </div>

        {/* Question Review */}
        <div className="space-y-4 mb-8">
          <h3 className="font-semibold text-gray-900">Question Review</h3>
          {answers.map((item, index) => (
            <div key={index} className="bg-white border rounded-xl p-4">
              <div className="flex items-start justify-between mb-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  item.question.type === 'technical' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-green-100 text-green-700'
                }`}>
                  Q{index + 1}: {item.question.type}
                </span>
                <span className={`font-semibold ${
                  item.feedback.score >= 8 ? 'text-green-600' :
                  item.feedback.score >= 6 ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {item.feedback.score}/10
                </span>
              </div>
              <p className="text-sm text-gray-700 font-medium mb-1">{item.question.question}</p>
              <p className="text-xs text-gray-500 line-clamp-2">{item.answer}</p>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <button
            onClick={handleNewInterview}
            className="flex-1 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all"
          >
            Start New Interview
          </button>
          <Link
            to="/tracker"
            className="flex-1 py-4 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all text-center"
          >
            Back to Job Tracker
          </Link>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="sticky top-0 z-40 bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src={logo} alt="AlignifyNOW" className="h-[80px] w-auto" />
              <span className="text-lg font-bold tracking-tight text-slate-900 hidden sm:inline">
                Alignify<span className="text-blue-600">NOW</span>
              </span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/" className="text-sm text-gray-600 hover:text-gray-900 hidden sm:block">
                Match Score
              </Link>
              <Link to="/tracker" className="text-sm text-gray-600 hover:text-gray-900 hidden sm:block">
                Job Tracker
              </Link>
              <Link to="/pricing" className="text-sm text-gray-600 hover:text-gray-900 hidden sm:block">
                Pricing
              </Link>
              <CreditCounter />
            </div>
          </div>
        </div>
      </nav>

      {/* Page Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Mock Interview</h1>
              <p className="text-gray-600">Practice with AI-powered interview questions</p>
            </div>
            {step !== 'setup' && step !== 'complete' && (
              <button
                onClick={() => {
                  if (window.confirm('Are you sure you want to end this interview?')) {
                    handleNewInterview();
                  }
                }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                End Interview
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {step === 'setup' && renderSetup()}
        {step === 'interview' && renderInterview()}
        {step === 'feedback' && renderFeedback()}
        {step === 'complete' && renderComplete()}
      </div>
    </div>
  );
};

export default MockInterview;
